// نموذج إنشاء/تعديل عجلة داخل لوحة الأدمن.
// يدعم: كل حقول العجلة، مصفوفات (سلاح/شخصية)، رفع صورة إلى GridFS.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, formatApiError } from "../lib/api";
import { resolveImageUrl } from "../lib/eventsUtils";
import {
  ImagePlus,
  Loader2,
  Save,
  X,
  Upload,
  ImageOff,
  Plus,
  Trash2,
} from "lucide-react";

const WHEEL_OPTIONS = [
  { value: "", label: "— لا يوجد —" },
  { value: "legendary_weapon", label: "السلاح الأسطوري (10 سحبات)" },
  { value: "legendary_character", label: "الشخصية الأسطورية (10 سحبات)" },
  { value: "mythic_weapon", label: "سلاح مثك (10 سحبات)" },
  { value: "mythic_character", label: "شخصية مثك (10 سحبات)" },
  { value: "wheel_7_draws", label: "عجلة 7 سحبات" },
  { value: "weapon_series", label: "سلسلة أسلحة (75 سحبة)" },
  { value: "weapons_series_75", label: "سلسلة أسلحة 75" },
  { value: "characters_series_100", label: "سلسلة شخصيات 100" },
];

const CALC_TYPE_OPTIONS = [
  { value: "", label: "— افتراضي —" },
  { value: "dual-weapons", label: "المزدوجة (أسلحة)" },
  { value: "dual-characters", label: "المزدوجة (شخصيات)" },
];

// حقل قائمة نصوص متعدد (يدعم إضافة/حذف/إعادة الترتيب البسيط).
const MultiTextField = ({ label, testIdPrefix, values, onChange }) => {
  const list = Array.isArray(values) ? values : [];
  const update = (idx, v) => {
    const next = [...list];
    next[idx] = v;
    onChange(next);
  };
  const add = () => onChange([...list, ""]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-white/70 text-[11px] font-bold">{label}</label>
        <button
          type="button"
          data-testid={`${testIdPrefix}-add`}
          onClick={add}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-lg bg-[#C9A96A]/10 hover:bg-[#C9A96A]/20 border border-[#C9A96A]/30 text-[#E5CB94] text-[10px] font-bold transition"
        >
          <Plus className="w-3 h-3" />
          إضافة
        </button>
      </div>
      {list.length === 0 ? (
        <div className="text-white/40 text-[11px] py-2">لا توجد عناصر — اضغط "إضافة".</div>
      ) : (
        <div className="space-y-2">
          {list.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                dir="auto"
                value={v}
                onChange={(e) => update(i, e.target.value)}
                data-testid={`${testIdPrefix}-input-${i}`}
                className="flex-1 h-9 rounded-lg bg-white/[0.03] border border-white/10 text-white px-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
                placeholder={`${label} ${i + 1}`}
              />
              <button
                type="button"
                data-testid={`${testIdPrefix}-remove-${i}`}
                onClick={() => remove(i)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// يحوّل حقل mainWeapon/character إلى مصفوفة أو نص أو null.
const normalizeArrayOrString = (arr) => {
  const cleaned = (arr || []).map((s) => (s || "").trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  if (cleaned.length === 1) return cleaned[0];
  return cleaned;
};

const toArray = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) return v;
  return [v];
};

// يحوّل ISO مثل "2026-08-30T00:00:00Z" إلى "2026-08-30" (لعرض تاريخ بدون وقت).
const isoToDateInput = (iso) => {
  if (!iso) return "";
  return iso.slice(0, 10);
};
// يحوّل قيمة الحقل "YYYY-MM-DD" إلى ISO عند 00:00:00Z (3ص السعودية).
const dateInputToIso = (v) => {
  if (!v) return null;
  return `${v}T00:00:00Z`;
};

const EventFormModal = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => normalizeFromInitial(initial));
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(normalizeFromInitial(initial));
    setError("");
  }, [initial]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const imagePreview = useMemo(() => resolveImageUrl(form.image), [form.image]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({
        ...prev,
        image: data.url,
        imageId: data.id,
      }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClearImage = () => {
    setField("image", null);
    setField("imageId", null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("اسم العجلة مطلوب.");
    if (!form.type.trim()) return setError("نوع العجلة مطلوب.");
    if (!form.endsAt) return setError("تاريخ النهاية مطلوب.");

    const payload = {
      name: form.name.trim(),
      type: form.type.trim(),
      wheelId: form.wheelId || null,
      calculatorType: form.calculatorType || null,
      image: form.image || null,
      imageId: form.imageId || null,
      startsAt: form.startsAt || null,
      endsAt: form.endsAt,
      durationDays: form.durationDays ? Number(form.durationDays) : null,
      mainWeapon: normalizeArrayOrString(form.mainWeapons),
      character: normalizeArrayOrString(form.characters),
      description: (form.description || "").trim(),
      featured: !!form.featured,
      meta: form.meta && form.meta.length ? form.meta : null,
      sections: form.sections && form.sections.length ? form.sections : null,
    };

    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { data } = await adminApi.patch(`/admin/events/${initial.id}`, payload);
        saved = data;
      } else {
        const withId = { ...payload };
        if (form.id.trim()) withId.id = form.id.trim();
        const { data } = await adminApi.post(`/admin/events`, withId);
        saved = data;
      }
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" dir="rtl">
      <form
        onSubmit={handleSubmit}
        data-testid="event-form"
        className="w-full max-w-3xl my-6 rounded-2xl bg-gradient-to-b from-[#0F1D42] to-[#0A1128] border border-[#C9A96A]/20 shadow-2xl"
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/5">
          <h3 className="text-white text-lg sm:text-xl font-black">
            {isEdit ? `تعديل: ${initial.name}` : "إضافة عجلة جديدة"}
          </h3>
          <button
            type="button"
            data-testid="event-form-close"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Image uploader */}
          <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3">
            <label className="text-white/70 text-[11px] font-bold mb-2 block">صورة العجلة</label>
            <div className="flex items-center gap-3">
              <div className="w-28 h-20 rounded-lg overflow-hidden bg-[#060B1E] border border-white/10 flex-shrink-0 flex items-center justify-center">
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="w-6 h-6 text-white/30" />
                )}
              </div>
              <div className="flex-1 flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  data-testid="event-image-file"
                  onChange={handleUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  data-testid="event-image-upload"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#C9A96A]/15 hover:bg-[#C9A96A]/25 border border-[#C9A96A]/40 text-[#E5CB94] text-xs font-bold transition disabled:opacity-40"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {form.image ? "تغيير الصورة" : "رفع صورة"}
                </button>
                {form.image && (
                  <button
                    type="button"
                    data-testid="event-image-clear"
                    onClick={handleClearImage}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/70 hover:text-red-200 text-xs font-bold transition"
                  >
                    <X className="w-3.5 h-3.5" />
                    إزالة
                  </button>
                )}
                <span className="text-white/40 text-[10px]">JPEG / PNG / WEBP — حتى 8MB</span>
              </div>
            </div>
          </div>

          {/* Basic fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldText label="الاسم" testId="event-name" value={form.name} onChange={(v) => setField("name", v)} required />
            <FieldText label="النوع" testId="event-type" value={form.type} onChange={(v) => setField("type", v)} required />

            <FieldSelect label="فئة العجلة (لحاسبة CP)" testId="event-wheelId" value={form.wheelId || ""} onChange={(v) => setField("wheelId", v)} options={WHEEL_OPTIONS} />
            <FieldSelect label="نوع الحاسبة (اختياري)" testId="event-calculatorType" value={form.calculatorType || ""} onChange={(v) => setField("calculatorType", v)} options={CALC_TYPE_OPTIONS} />

            <FieldDate label="تاريخ البداية (3ص السعودية)" testId="event-startsAt" value={isoToDateInput(form.startsAt)} onChange={(v) => setField("startsAt", dateInputToIso(v))} />
            <FieldDate label="تاريخ النهاية (3ص السعودية) *" testId="event-endsAt" value={isoToDateInput(form.endsAt)} onChange={(v) => setField("endsAt", dateInputToIso(v))} required />

            <FieldNumber label="مدة التوفر (أيام)" testId="event-durationDays" value={form.durationDays || ""} onChange={(v) => setField("durationDays", v)} />
            <FieldToggle label="عجلة مميزة (★)" testId="event-featured" value={form.featured} onChange={(v) => setField("featured", v)} />
          </div>

          <MultiTextField label="الأسلحة الرئيسية" testIdPrefix="event-mainWeapons" values={form.mainWeapons} onChange={(v) => setField("mainWeapons", v)} />
          <MultiTextField label="الشخصيات" testIdPrefix="event-characters" values={form.characters} onChange={(v) => setField("characters", v)} />

          <div>
            <label className="text-white/70 text-[11px] font-bold mb-1 block">الوصف</label>
            <textarea
              value={form.description}
              data-testid="event-description"
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              dir="auto"
              className="w-full rounded-xl bg-white/[0.03] border border-white/10 text-white p-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
              placeholder="وصف مختصر يظهر في المودال..."
            />
          </div>

          {!isEdit && (
            <FieldText
              label="معرّف مخصّص (اختياري - يُولَّد تلقائيًا)"
              testId="event-id"
              value={form.id}
              onChange={(v) => setField("id", v)}
              placeholder="مثال: viridescent_oath"
            />
          )}

          {error && (
            <div data-testid="event-form-error" className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 sm:p-5 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 text-sm font-bold transition"
          >
            إلغاء
          </button>
          <button
            type="submit"
            data-testid="event-form-save"
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-black text-sm hover:brightness-110 transition disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "حفظ التعديلات" : "إنشاء العجلة"}
          </button>
        </div>
      </form>
    </div>
  );
};

// حقول مساعدة
const FieldText = ({ label, testId, value, onChange, required, placeholder }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">
      {label} {required && <span className="text-red-300">*</span>}
    </label>
    <input
      type="text"
      dir="auto"
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
    />
  </div>
);

const FieldNumber = ({ label, testId, value, onChange }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">{label}</label>
    <input
      type="number"
      min={0}
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
    />
  </div>
);

const FieldDate = ({ label, testId, value, onChange, required }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">
      {label} {required && <span className="text-red-300">*</span>}
    </label>
    <input
      type="date"
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
    />
  </div>
);

const FieldSelect = ({ label, testId, value, onChange, options }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">{label}</label>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#0A1128]">
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const FieldToggle = ({ label, testId, value, onChange }) => (
  <div>
    <label className="text-white/70 text-[11px] font-bold mb-1 block">{label}</label>
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`w-full h-10 rounded-xl border text-sm font-bold transition ${
        value
          ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
          : "bg-white/[0.03] border-white/10 text-white/60 hover:border-[#C9A96A]/25"
      }`}
    >
      {value ? "مفعّل" : "معطّل"}
    </button>
  </div>
);

// يحوّل event قادم من الـAPI إلى شكل النموذج المستخدم داخل الفورم.
function normalizeFromInitial(initial) {
  return {
    id: initial?.id || "",
    name: initial?.name || "",
    type: initial?.type || "",
    wheelId: initial?.wheelId || "",
    calculatorType: initial?.calculatorType || "",
    image: initial?.image || null,
    imageId: initial?.imageId || null,
    startsAt: initial?.startsAt || null,
    endsAt: initial?.endsAt || "",
    durationDays: initial?.durationDays ?? "",
    mainWeapons: toArray(initial?.mainWeapon),
    characters: toArray(initial?.character),
    description: initial?.description || "",
    featured: !!initial?.featured,
    meta: initial?.meta || null,
    sections: initial?.sections || null,
  };
}

export default EventFormModal;
